import { darken } from 'polished';
import { defineMessages, useIntl } from 'react-intl';
import styled, { css, type DefaultTheme } from 'styled-components';
import { useMixpanel } from '../../../../contexts';
import { useDynamicCallback } from '../../../../hooks';
import { setAlpha } from '../../../../styles';
import { MixpanelEvent } from '../../../../tokens';
import { Button, ButtonVariants } from '../../../Button';
import { getButtonBorderRadius } from '../../../Button/utils';
import { FormControlSizes } from '../../../Form';
import { ICON_SIZES, Icon, IconName } from '../../../Icons';
import { FormattedMessage } from '../../../Intl';
import { Text } from '../../../Text';
import { Tooltip } from '../../../Tooltip';

const messages = defineMessages({
  filters: {
    defaultMessage: 'Filters',
    id: 'Filters.FilterBuilder.filters',
  },
  clearAllFilters: {
    defaultMessage: 'Clear all filters',
    id: 'Filters.FilterBuilder.clearAllFilters',
  },
  clearAllActiveFilters: {
    defaultMessage: 'Clear all {count, plural, one {# active filter} other {# active filters}}',
    id: 'Filters.FilterBuilder.clearAllActiveFilters',
  },
  loadingReferenceData: {
    defaultMessage: 'Loading reference data for Filters',
    id: 'Filters.FilterBuilder.loadingReferenceData',
  },
});

export interface FilterBuilderToggleButtonProps {
  activeFilterCount: number;
  isOpen: boolean;
  onClick: () => void;
  removeAllFilterClauses: () => void;
  showClearButton?: boolean;
  size?: FormControlSizes;
  disabled?: boolean;
}

export const FilterBuilderToggleButton = ({
  activeFilterCount,
  isOpen,
  onClick,
  removeAllFilterClauses,
  showClearButton = true,
  size = FormControlSizes.Small,
  disabled = false,
}: FilterBuilderToggleButtonProps) => {
  const mixpanel = useMixpanel();
  const { formatMessage } = useIntl();

  const handleOnClick = useDynamicCallback(() => {
    onClick();
    if (!isOpen) {
      mixpanel.track(MixpanelEvent.ClickOpenFilterBuilderButton);
    }
  });

  const hasActiveFilters = showClearButton && activeFilterCount > 0;

  return (
    <Tooltip
      tooltip={disabled ? formatMessage(messages.loadingReferenceData) : undefined}
      usePortal
      tooltipTestID="filter-builder-loading-tooltip"
    >
      <FilterBuilderControl $open={isOpen} $size={size} role="group" aria-label="Filters">
        <FilterBuilderButton
          variant={isOpen ? ButtonVariants.Primary : ButtonVariants.Default}
          data-is-expanded={isOpen}
          aria-expanded={isOpen}
          size={size}
          startIcon={IconName.Filter}
          onClick={handleOnClick}
          disabled={disabled}
          data-testid="filter-builder-toggle-button"
        >
          <Text weight="500">
            <FormattedMessage {...messages.filters} />
          </Text>
        </FilterBuilderButton>
        {hasActiveFilters && (
          <Tooltip tooltip={formatMessage(messages.clearAllFilters)} usePortal>
            <ClearFiltersButton
              type="button"
              $open={isOpen}
              aria-label={formatMessage(messages.clearAllActiveFilters, { count: activeFilterCount })}
              onClick={removeAllFilterClauses}
              data-testid="filter-builder-clear-button"
            >
              {activeFilterCount}
              <Icon icon={IconName.Close} size={ICON_SIZES.TINY} />
            </ClearFiltersButton>
          </Tooltip>
        )}
      </FilterBuilderControl>
    </Tooltip>
  );
};

const controlColors = (theme: DefaultTheme, $open: boolean) =>
  $open
    ? css`
        background: ${theme.backgroundPrimaryButton};
        border-color: ${theme.borderColorPrimaryButton};
        box-shadow: ${theme.boxShadowPrimaryButton};
        color: ${theme.colorTextPrimaryButton};

        &:hover {
          background: ${theme.backgroundPrimaryButtonHover};
          border-color: ${theme.borderColorPrimaryButtonHover};
          box-shadow: ${theme.boxShadowPrimaryButtonHover};
          color: ${theme.colorTextPrimaryButtonHover};
        }

        &:focus-within {
          border-color: ${theme.borderColorPrimaryButtonFocus};
          box-shadow: ${theme.boxShadowPrimaryButtonFocus};
        }
      `
    : css`
        background: ${theme.backgroundDefaultButton};
        border-color: ${theme.borderColorDefaultButton};
        box-shadow: ${theme.boxShadowDefaultButton};
        color: ${theme.colorTextDefaultButton};

        &:hover {
          background: ${theme.backgroundDefaultButtonHover};
          border-color: ${theme.borderColorDefaultButtonHover};
          box-shadow: ${theme.boxShadowDefaultButtonHover};
          color: ${theme.colorTextDefaultButtonHover};
        }

        &:focus-within {
          border-color: ${theme.borderColorDefaultButtonFocus};
          box-shadow: ${theme.boxShadowDefaultButtonFocus};
        }
      `;

const FilterBuilderControl = styled.div<{ $open: boolean; $size: FormControlSizes }>`
  display: inline-flex;
  align-items: center;
  border: solid ${({ theme }) => theme.borderWidthButton}px;
  border-radius: ${({ theme, $size }) => getButtonBorderRadius(theme, $size)}px;
  transition:
    background 200ms ease,
    border-color 200ms ease,
    box-shadow 200ms ease,
    color 200ms ease;

  ${({ theme, $open }) => controlColors(theme, $open)}
`;

const FilterBuilderButton = styled(Button)`
  border-width: 0;

  &&,
  &&:hover,
  &&:focus,
  &&:active {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    color: inherit;
  }
`;

const ClearFiltersButton = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing2}px;
  height: ${({ theme }) => theme.spacing16}px;
  margin: ${({ theme }) => theme.spacing2}px ${({ theme }) => theme.spacing4}px ${({ theme }) => theme.spacing2}px 0;
  padding: 0 ${({ theme }) => theme.spacing4}px;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadiusSmall}px;
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  line-height: 1;
  cursor: pointer;

  i {
    line-height: 1;
  }

  ${({ theme, $open }) =>
    $open
      ? css`
          background: ${setAlpha(0.18, theme.colors.white.default)};
          color: ${theme.colorTextPrimaryButton};

          &:hover {
            background: ${setAlpha(0.32, theme.colors.white.default)};
          }
        `
      : css`
          background: ${theme.colors.orange.default};
          color: ${theme.colors.white.default};

          &:hover {
            background: ${darken(0.08, theme.colors.orange.default)};
          }
        `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colorTextAttention};
    outline-offset: 1px;
  }
`;
